// FE-SCRM-048 — CSV template generators for the Download template
// buttons in the upload step.
//
// The headers are pinned to BE-SCRM-047's accepted column set (see
// backend/sales/csv_imports.py: _PROSPECT_REQUIRED + _PROSPECT_OPTIONAL
// and the _SALES_CONTACT_* equivalents). A producer who clicks the
// button gets a file that imports cleanly without any header edits.

import type { ImportType } from "./types";


// Order matters: keeps required columns first so a producer scanning
// the file in Excel sees them without scrolling.
export const PROSPECT_TEMPLATE_HEADERS: readonly string[] = [
  "name",
  "renewal_month",
  "renewal_year",
  "ein",
  "lives_band",
  "employee_count",
  "industry",
  "address",
  "city",
  "state",
  "zip",
  "current_broker_firm",
  "current_broker_office",
  "current_broker_relationship_strength",
  "source",
  "tags",
  "notes",
] as const;


// Demo row tuned to demonstrate a high-fit prospect: mid-market lives
// band, Form-5500-matchable EIN, weak incumbent relationship — the
// archetype the BE-SCRM-018 fit-score helper rewards.
const PROSPECT_DEMO_ROW: readonly string[] = [
  "Acme Manufacturing Co",
  "October",
  "2026",
  "12-3456789",
  "mid_market",
  "320",
  "Manufacturing",
  "1200 Industrial Way",
  "Detroit",
  "MI",
  "48201",
  "Anchor Benefits Group",
  "Cleveland",
  "weak",
  "referral",
  "manufacturing;midwest",
  "Met at SHRM conference; finance VP unhappy with current renewal.",
] as const;


export const SALES_CONTACT_TEMPLATE_HEADERS: readonly string[] = [
  "prospect_name",
  "first_name",
  "last_name",
  "title",
  "email",
  "phone",
  "mobile",
  "decision_role",
  "influence_level",
  "stance",
] as const;


const SALES_CONTACT_DEMO_ROW: readonly string[] = [
  "Acme Manufacturing Co",
  "Jordan",
  "Reyes",
  "VP People",
  "jordan.reyes@acme.example",
  "313-555-0184",
  "313-555-0142",
  "economic_buyer",
  "high",
  "supportive",
] as const;


function _escapeCell(value: string): string {
  // Wrap any cell containing comma / quote / newline in quotes and
  // double-up internal quotes (RFC 4180). Our demo rows are tuned to
  // avoid these characters but we still pay the cost so future edits
  // can't silently break the template.
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}


function _buildCsv(
  headers: readonly string[], demo: readonly string[],
): string {
  const header_line = headers.map(_escapeCell).join(",");
  const demo_line = demo.map(_escapeCell).join(",");
  return `${header_line}\n${demo_line}\n`;
}


export function buildProspectsTemplate(): string {
  return _buildCsv(PROSPECT_TEMPLATE_HEADERS, PROSPECT_DEMO_ROW);
}


export function buildSalesContactsTemplate(): string {
  return _buildCsv(SALES_CONTACT_TEMPLATE_HEADERS, SALES_CONTACT_DEMO_ROW);
}


export interface TemplateBundle {
  csv: string;
  filename: string;
}


export function templateForType(import_type: ImportType): TemplateBundle {
  if (import_type === "prospects") {
    return {
      csv: buildProspectsTemplate(),
      filename: "prospects_import_template.csv",
    };
  }
  return {
    csv: buildSalesContactsTemplate(),
    filename: "sales_contacts_import_template.csv",
  };
}


// Triggers a download on click without leaving the page — we
// construct a Blob URL, anchor it, click programmatically, then
// revoke the URL on the next tick. The anchor is appended to the
// document so Safari treats the click as a real navigation.
export function downloadTemplate(import_type: ImportType): void {
  const bundle = templateForType(import_type);
  const blob = new Blob([bundle.csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = bundle.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // queueMicrotask is enough — Safari has finished reading the href
  // by the time the click handler returns.
  queueMicrotask(() => URL.revokeObjectURL(url));
}
